import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Check, Search, Eye, EyeOff, AlertCircle } from 'lucide-react';

// ============================================
// TEXT INPUT COMPONENT
// ============================================
const TextInput = ({ 
  label, 
  value, 
  onChange, 
  placeholder = '', 
  type = 'text',
  error = '',
  disabled = false,
  required = false,
  icon = null
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputType = type === 'password' && showPassword ? 'text' : type;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-medium text-[#191d23]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]">
            {icon}
          </div>
        )}
        <input
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full h-10 px-3 ${icon ? 'pl-10' : ''} py-2.5 rounded border text-base transition-all
            ${error 
              ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100' 
              : 'border-[#d8dae5] focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100'
            }
            ${disabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-[#191d23]'}
            placeholder:text-slate-400 focus:outline-none`}
        />
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#191d23] transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
};

// ============================================
// TEXTAREA COMPONENT
// ============================================
const TextArea = ({ 
  label, 
  value, 
  onChange, 
  placeholder = '', 
  rows = 4,
  error = '',
  required = false,
  maxLength = null
}) => {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-medium text-[#191d23]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className={`w-full px-3 py-2.5 rounded border text-base transition-all resize-none
          ${error 
            ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100' 
            : 'border-[#d8dae5] focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100'
          }
          placeholder:text-slate-400 focus:outline-none`}
      />
      <div className="flex justify-between items-center">
        {error ? (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <AlertCircle size={12} />
            {error}
          </p>
        ) : <span />}
        {maxLength && (
          <p className="text-xs text-[#64748B]">
            {value.length}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================
// SELECT DROPDOWN COMPONENT
// ============================================
const Select = ({ 
  label, 
  value, 
  onChange, 
  options = [], 
  placeholder = 'Select option',
  error = '',
  required = false,
  disabled = false
}) => {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-medium text-[#191d23]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`w-full h-10 px-3 pr-10 py-2 rounded border text-base appearance-none transition-all
            ${error 
              ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100' 
              : 'border-[#d8dae5] focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100'
            }
            ${disabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-[#191d23]'}
            focus:outline-none cursor-pointer`}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown 
          size={20} 
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none"
        />
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
};

// ============================================
// RADIO BUTTON GROUP COMPONENT
// ============================================
const RadioGroup = ({ 
  label, 
  options = [], 
  selected, 
  onChange, 
  direction = 'vertical',
  required = false 
}) => {
  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label className="text-sm font-medium text-[#191d23]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className={`flex ${direction === 'horizontal' ? 'flex-row gap-4' : 'flex-col gap-2'}`}>
        {options.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div className="relative w-5 h-5">
              <input
                type="radio"
                value={option.value}
                checked={selected === option.value}
                onChange={(e) => onChange(e.target.value)}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 transition-all
                ${selected === option.value 
                  ? 'border-emerald-700 bg-white' 
                  : 'border-[#d8dae5] bg-white group-hover:border-emerald-500'
                }`}
              >
                {selected === option.value && (
                  <div className="absolute inset-0 m-auto w-3 h-3 rounded-full bg-emerald-700" />
                )}
              </div>
            </div>
            <span className="text-base text-[#191d23]">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// ============================================
// TOGGLE SWITCH COMPONENT
// ============================================
const Toggle = ({ 
  label, 
  checked, 
  onChange, 
  description = '',
  disabled = false 
}) => {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex flex-col gap-1">
        {label && (
          <label className="text-sm font-medium text-[#191d23]">
            {label}
          </label>
        )}
        {description && (
          <p className="text-xs text-[#64748B]">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative w-11 h-6 rounded-full transition-all
          ${checked ? 'bg-emerald-700' : 'bg-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform
          ${checked ? 'translate-x-5' : 'translate-x-0'}
        `} />
      </button>
    </div>
  );
};

// ============================================
// SEARCH INPUT COMPONENT
// ============================================
const SearchInput = ({ 
  value, 
  onChange, 
  placeholder = 'Search...', 
  onClear 
}) => {
  return (
    <div className="relative">
      <Search 
        size={18} 
        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 pl-10 pr-10 py-2.5 rounded border border-[#d8dae5] text-base
          placeholder:text-slate-400 focus:outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100
          transition-all"
      />
      {value && (
        <button
          onClick={onClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#191d23] transition-colors"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
};

// ============================================
// NUMBER INPUT COMPONENT
// ============================================
const NumberInput = ({ 
  label, 
  value, 
  onChange, 
  min = 0, 
  max = 999999,
  step = 1,
  placeholder = '0',
  error = '',
  required = false 
}) => {
  const increment = () => {
    const newValue = Math.min((parseFloat(value) || 0) + step, max);
    onChange(newValue);
  };

  const decrement = () => {
    const newValue = Math.max((parseFloat(value) || 0) - step, min);
    onChange(newValue);
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-medium text-[#191d23]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative flex">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          className={`flex-1 h-10 px-3 py-2.5 rounded-l border text-base
            ${error 
              ? 'border-red-500 focus:border-red-500' 
              : 'border-[#d8dae5] focus:border-emerald-700'
            }
            focus:outline-none focus:ring-2 focus:ring-emerald-100 transition-all`}
        />
        <div className="flex flex-col border-t border-b border-r border-[#d8dae5] rounded-r overflow-hidden">
          <button
            type="button"
            onClick={increment}
            className="px-2 bg-gray-50 hover:bg-gray-100 transition-colors border-b border-[#d8dae5]"
          >
            <ChevronUp size={14} className="text-[#64748B]" />
          </button>
          <button
            type="button"
            onClick={decrement}
            className="px-2 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <ChevronDown size={14} className="text-[#64748B]" />
          </button>
        </div>
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
};


const Button = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'medium',
  disabled = false,
  fullWidth = false,
  icon = null,
  loading = false
}) => {
  const variants = {
    primary: 'bg-emerald-700 hover:bg-emerald-800 text-white',
    secondary: 'bg-white hover:bg-gray-50 text-[#191d23] border border-[#d8dae5]',
    outline: 'bg-transparent hover:bg-emerald-50 text-emerald-700 border border-emerald-700',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    ghost: 'bg-transparent hover:bg-gray-100 text-[#191d23]'
  };

  const sizes = {
    small: 'h-8 px-3 text-sm',
    medium: 'h-10 px-4 text-base',
    large: 'h-12 px-6 text-lg'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        flex items-center justify-center gap-2 rounded font-semibold
        transition-all disabled:opacity-50 disabled:cursor-not-allowed
      `}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
      ) : (
        <>
          {icon && icon}
          {children}
        </>
      )}
    </button>
  );
};

// Main Filter Panel Component
const FilterPanel = ({ onApply, onReset }) => {
  const [filters, setFilters] = useState({
    intersections: ['90 Mt Auburn St, Cambridge, MA 02138'],
    dateRange: { start: '', end: '' },
    timeRange: { start: '10:00am', end: '10:00pm' },
    vehicles: ['Car', 'Bike', 'Truck']
  });

  const intersections = [
    '90 Mt Auburn St, Cambridge, MA 02138',
    '123 Main St, Boston, MA 02101',
    '456 Park Ave, Cambridge, MA 02139',
    '789 Harvard St, Cambridge, MA 02140',
    '321 MIT Ave, Cambridge, MA 02142'
  ];

  const vehicles = ['Car', 'Bike', 'Bus', 'Pedestrian', 'Truck'];

  return (
    <div className="flex flex-col gap-4 p-6 w-[330px] h-[744px] bg-white rounded shadow-lg">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-[22px] font-bold text-[#191d23]">Filters</h2>
        <button
          onClick={onReset}
          className="w-6 h-6 flex items-center justify-center p-2 rounded border border-[#cfd7ce] hover:bg-gray-50 transition-colors"
        >
          <X size={16} className="text-[#64748B]" />
        </button>
      </div>

      {/* Intersection Selector */}
      <CheckboxSection
        title="Select Intersection"
        options={intersections}
        selected={filters.intersections}
        onChange={(selected) => setFilters({ ...filters, intersections: selected })}
      />

      {/* Date and Time Section */}
      <DateTimeSection
        dateRange={filters.dateRange}
        timeRange={filters.timeRange}
        onDateChange={(dateRange) => setFilters({ ...filters, dateRange })}
        onTimeChange={(timeRange) => setFilters({ ...filters, timeRange })}
      />

      {/* Vehicle Selector */}
      <TagSelector
        title="Select Vehicle"
        options={vehicles}
        selected={filters.vehicles}
        onChange={(selected) => setFilters({ ...filters, vehicles: selected })}
      />

      {/* Continue Button */}
      <button
        onClick={() => onApply(filters)}
        className="flex justify-center items-center gap-1 h-11 px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 rounded transition-colors"
      >
        <span className="text-base font-semibold text-white">Continue</span>
        <ChevronDown size={20} className="text-white rotate-[-90deg]" />
      </button>
    </div>
  );
};

// Checkbox Section with Show More
const CheckboxSection = ({ title, options, selected, onChange, maxVisible = 2 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleOptions = isExpanded ? options : options.slice(0, maxVisible);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(item => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4 rounded border border-[#e3eae2]">
      <div className="flex justify-between items-start">
        <p className="text-base font-medium text-[#191d23]">{title}</p>
        <ChevronDown size={20} className="text-[#64748B]" />
      </div>

      {visibleOptions.map((option) => (
        <div
          key={option}
          onClick={() => toggleOption(option)}
          className="flex items-center gap-3 cursor-pointer"
        >
          <div className="flex-shrink-0 w-4 h-4">
            {selected.includes(option) ? (
              <div className="w-4 h-4 rounded bg-[#339903] flex items-center justify-center">
                <Check size={12} className="text-white" />
              </div>
            ) : (
              <div className="w-4 h-4 rounded-sm border-2 border-[#040921]/[0.32]" />
            )}
          </div>
          <p className="text-base text-[#191d23]">{option}</p>
        </div>
      ))}

      {options.length > maxVisible && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm font-semibold text-[#005a00] hover:text-[#007a00] transition-colors"
        >
          {isExpanded ? 'Show less' : 'Show more'}
          {isExpanded ? (
            <ChevronUp size={20} className="text-[#005a00]" />
          ) : (
            <ChevronDown size={20} className="text-[#005a00]" />
          )}
        </button>
      )}
    </div>
  );
};

// Date and Time Section with integrated picker
const DateTimeSection = ({ dateRange, timeRange, onDateChange, onTimeChange }) => {
  return (
    <div className="flex flex-col gap-3 p-4 rounded border border-[#e3eae2]">
      <div className="flex items-center justify-between gap-3">
        {/* From DateTime */}
        <DateTimePicker
          date={dateRange.start}
          time={timeRange.start}
          onDateChange={(date) => onDateChange({ ...dateRange, start: date })}
          onTimeChange={(time) => onTimeChange({ ...timeRange, start: time })}
        />

        {/* Arrow Button */}
        <button className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#86C540] hover:bg-[#75b330] rounded-full transition-colors">
          <ChevronDown size={20} className="text-white rotate-[-90deg]" />
        </button>

        {/* To DateTime */}
        <DateTimePicker
          date={dateRange.end}
          time={timeRange.end}
          onDateChange={(date) => onDateChange({ ...dateRange, end: date })}
          onTimeChange={(time) => onTimeChange({ ...timeRange, end: time })}
        />
      </div>
    </div>
  );
};

// Individual DateTime Picker Component
const DateTimePicker = ({ date, time, onDateChange, onTimeChange }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return { dayName: '', monthDay: '' };
    const d = new Date(dateStr);
    return {
      dayName: d.toLocaleDateString('en-US', { weekday: 'long' }),
      monthDay: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };
  };

  const handleDateSelect = (e) => {
    onDateChange(e.target.value);
    setShowDatePicker(false);
  };

  const { dayName, monthDay } = formatDate(date);

  return (
    <div className="flex flex-col items-center gap-1 flex-1 relative">
      {/* Day Name */}
      <p className="text-xs text-[#64748B]">{dayName || 'Select date'},</p>
      
      {/* Date Display - Clickable */}
      <button 
        onClick={() => setShowDatePicker(!showDatePicker)}
        className="text-2xl font-semibold text-[#191d23] hover:text-emerald-700 transition-colors"
      >
        {monthDay || 'Mar 19'}
      </button>

      {/* Time Display - Clickable */}
      <button
        onClick={() => setShowTimePicker(!showTimePicker)}
        className="text-sm text-[#64748B] hover:text-emerald-700 transition-colors"
      >
        {time || '9:00 AM'}
      </button>

      {/* Date Picker Dropdown */}
      {showDatePicker && (
        <div className="absolute top-full mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <input
            type="date"
            value={date}
            onChange={handleDateSelect}
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-700"
          />
        </div>
      )}

      {/* Time Picker Dropdown */}
      {showTimePicker && (
        <div className="absolute top-full mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {generateTimeOptions().map((timeOption) => (
            <button
              key={timeOption}
              onClick={() => {
                onTimeChange(timeOption);
                setShowTimePicker(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-[#191d23] hover:bg-emerald-50 transition-colors whitespace-nowrap"
            >
              {timeOption}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Tag Selector Component
const TagSelector = ({ title, options, selected, onChange }) => {
  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(item => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const selectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4 rounded border border-[#e7eaee]">
      <div className="flex justify-between items-start">
        <p className="text-base font-medium text-[#191d23]">{title}</p>
        <button
          onClick={selectAll}
          className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
        >
          {selected.length === options.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2.5">
        {options.map((option) => {
          const isSelected = selected.includes(option);
          return (
            <button
              key={option}
              onClick={() => toggleOption(option)}
              className={`flex items-center gap-3 px-2 py-1.5 rounded transition-colors ${
                isSelected
                  ? 'bg-emerald-50 text-emerald-900'
                  : 'bg-[#f7f8f9] text-[#191d23] font-light hover:bg-gray-200'
              }`}
            >
              {isSelected && (
                <Check size={20} className="text-[#064E3B]" />
              )}
              <span className="text-base">{option}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Helper function to generate time options
export const generateTimeOptions = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const period = hour >= 12 ? 'pm' : 'am';
      const displayHour = hour % 12 || 12;
      const displayMin = min.toString().padStart(2, '0');
      times.push(`${displayHour}:${displayMin}${period}`);
    }
  }
  return times;
};

// Demo App
export default function Filter() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    description: '',
    country: '',
    gender: '',
    notifications: true,
    searchQuery: '',
    quantity: 1
  });

  const handleApply = (filters) => {
    
    alert('Filters applied! Check console for details.');
  };

  const handleReset = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-[#191d23] mb-8">Dynamic Form Components Demo</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Filter Panel */}
          <FilterPanel onApply={handleApply} onReset={handleReset} />

          {/* Form Components Showcase */}
          <div className="flex flex-col gap-6">
            {/* Text Inputs */}
            <div className="bg-white rounded p-6 shadow-lg">
              <h2 className="text-xl font-bold text-[#191d23] mb-4">Text Inputs</h2>
              <div className="flex flex-col gap-4">
                <TextInput
                  label="Full Name"
                  value={formData.name}
                  onChange={(value) => setFormData({...formData, name: value})}
                  placeholder="Enter your name"
                  required
                />
                <TextInput
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(value) => setFormData({...formData, email: value})}
                  placeholder="you@example.com"
                  icon={<Search size={18} />}
                  error={formData.email && !formData.email.includes('@') ? 'Invalid email' : ''}
                />
                <TextInput
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(value) => setFormData({...formData, password: value})}
                  placeholder="Enter password"
                />
              </div>
            </div>

            {/* Select & Radio */}
            <div className="bg-white rounded p-6 shadow-lg">
              <h2 className="text-xl font-bold text-[#191d23] mb-4">Select & Radio</h2>
              <div className="flex flex-col gap-4">
                <Select
                  label="Country"
                  value={formData.country}
                  onChange={(value) => setFormData({...formData, country: value})}
                  options={[
                    { value: 'us', label: 'United States' },
                    { value: 'uk', label: 'United Kingdom' },
                    { value: 'ca', label: 'Canada' },
                    { value: 'au', label: 'Australia' }
                  ]}
                  placeholder="Select your country"
                  required
                />
                <RadioGroup
                  label="Gender"
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'other', label: 'Other' }
                  ]}
                  selected={formData.gender}
                  onChange={(value) => setFormData({...formData, gender: value})}
                  direction="horizontal"
                />
              </div>
            </div>

            {/* TextArea & Number */}
            <div className="bg-white rounded p-6 shadow-lg">
              <h2 className="text-xl font-bold text-[#191d23] mb-4">TextArea & Number</h2>
              <div className="flex flex-col gap-4">
                <TextArea
                  label="Description"
                  value={formData.description}
                  onChange={(value) => setFormData({...formData, description: value})}
                  placeholder="Enter description..."
                  maxLength={200}
                  rows={4}
                />
                <NumberInput
                  label="Quantity"
                  value={formData.quantity}
                  onChange={(value) => setFormData({...formData, quantity: value})}
                  min={1}
                  max={100}
                  step={1}
                />
              </div>
            </div>

            {/* Search & Toggle */}
            <div className="bg-white rounded p-6 shadow-lg">
              <h2 className="text-xl font-bold text-[#191d23] mb-4">Search & Toggle</h2>
              <div className="flex flex-col gap-4">
                <SearchInput
                  value={formData.searchQuery}
                  onChange={(value) => setFormData({...formData, searchQuery: value})}
                  placeholder="Search locations..."
                  onClear={() => setFormData({...formData, searchQuery: ''})}
                />
                <Toggle
                  label="Enable Notifications"
                  description="Receive email notifications for updates"
                  checked={formData.notifications}
                  onChange={(value) => setFormData({...formData, notifications: value})}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="bg-white rounded p-6 shadow-lg">
              <h2 className="text-xl font-bold text-[#191d23] mb-4">Buttons</h2>
              <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                  <Button variant="primary">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="outline">Outline</Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="danger">Danger</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="primary" disabled>Disabled</Button>
                </div>
                <Button variant="primary" fullWidth icon={<Check size={18} />}>
                  Full Width Button
                </Button>
                <Button variant="primary" loading>Loading...</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}